import React, { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { useSpring, animated } from "react-spring";
import { Link, useNavigate } from "react-router-dom";

const AIIntegration = ({ title, description }) => {
    const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const scrollToSection = (sectionName) => {
        const formattedId = sectionName.toLowerCase().replace(" ", "-");
        const section = document.getElementById(formattedId);

        if (section) {
            section.scrollIntoView({ behavior: "smooth" });
        }
    };

    const containerAnimation = useSpring({
        from: { transform: "translateX(100px)", opacity: 0 },
        to: {
            transform: inView ? "translateX(0)" : "translateX(100px)",
            opacity: inView ? 1 : 0,
        },
        config: { duration: 400 },
    });

    const textAnimation = useSpring({
        from: { opacity: 0 },
        to: { opacity: inView ? 1 : 0 },
        delay: 500,
        config: { duration: 1000 },
    });

    const nodes = [
        { x: 20, y: 20, size: 80, delay: "0s" },
        { x: 50, y: 150, size: 100, delay: "0.5s" },
        { x: 180, y: 80, size: 60, delay: "1s" },
        { x: 200, y: 200, size: 90, delay: "1.5s" },
    ];

    return (
        <animated.div
            ref={ref}
            style={{
                ...containerStyle,
                flexDirection: isMobile ? "column" : "row",
                height: isMobile ? "auto" : "340px",
                padding: isMobile ? "20px" : "0 24px",
                textAlign: isMobile ? "center" : "left",
                ...containerAnimation,
            }}
        >
            <style>
                {`
                    @keyframes bounceIn {
                        0% {
                            transform: scale(0.5);
                            opacity: 0;
                        }
                        60% {
                            transform: scale(1.2);
                            opacity: 1;
                        }
                        80% {
                            transform: scale(0.9);
                        }
                        100% {
                            transform: scale(1);
                        }
                    }
                `}
            </style>
            {/* Left Section – Textual Content */}
            <div style={styles.contentStyle}>
                <animated.h1
                    style={{
                        ...styles.titleStyle,
                        fontSize: isMobile ? "1.2rem" : "2rem",
                        marginBottom: isMobile ? '0.7rem' : '0.8rem',
                        ...textAnimation,
                    }}
                >
                    {title}
                </animated.h1>
                <animated.p
                    style={{
                        ...styles.descriptionStyle,
                        fontSize: isMobile ? "0.95rem" : "1.05rem",
                        marginBottom: isMobile ? '1.1rem' : '1.3rem',
                        ...textAnimation,
                    }}
                >
                    {description}
                </animated.p>
                <animated.button
                    style={{
                        ...styles.buttonStyle,
                        width: isMobile ? "100%" : "auto",
                        fontSize: isMobile ? '0.95rem' : '1rem',
                        padding: isMobile ? '0.7rem 1.2rem' : '0.8rem 1.5rem',
                        ...textAnimation,
                    }}
                    onMouseOver={(e) => {
                        e.target.style.backgroundColor = "#C9e8ff";
                        e.target.style.color = "#fff";
                        e.target.style.transform = "scale(1.05)";
                    }}
                    onMouseOut={(e) => {
                        e.target.style.backgroundColor = "#fff";
                        e.target.style.color = "#4b70f5";
                        e.target.style.transform = "scale(1)";
                    }}
                    onClick={() => navigate("/contact")}
                >
                    Discover More
                </animated.button>
            </div>
            {/* Right Section – Animated Dots */}
            <div
                style={{
                    ...styles.graphicStyle,
                    marginTop: isMobile ? "40px" : "0",
                    justifyContent: isMobile ? "center" : "flex-start",
                }}
            >
                <div
                    style={{
                        ...styles.circleContainerStyle,
                        width: isMobile ? "200px" : "300px",
                        height: isMobile ? "200px" : "300px",
                    }}
                >
                    {nodes.map((node, index) => (
                        <div
                            key={index}
                            style={{
                                ...styles.circleStyle,
                                width: `${isMobile ? node.size * 0.8 : node.size}px`,
                                height: `${isMobile ? node.size * 0.8 : node.size}px`,
                                top: `${isMobile ? node.y * 0.8 : node.y}px`,
                                left: `${isMobile ? node.x * 0.8 : node.x}px`,
                                animation: `bounceIn 1s ease forwards`,
                                animationDelay: node.delay,
                            }}
                        ></div>
                    ))}
                </div>
            </div>
        </animated.div>
    );
};

// Container style for the entire component
const containerStyle = {
    margin: "0 auto",
    maxWidth: "1200px",
    width: "100%",
    background: "linear-gradient(135deg, #b8baff 0%, #6666ff 50%, #7bd7f7 100%)",
    color: "#fff",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "200px",
};

const styles = {
    contentStyle: {
        flex: "1",
        maxWidth: "600px",
        padding: "1rem",
        boxSizing: "border-box",
    },
    titleStyle: {
        marginBottom: "1rem",
        lineHeight: "1.2",
        color: "#fff",
    },
    descriptionStyle: {
        marginBottom: "2rem",
        lineHeight: "1.5",
        color: "#fff",
    },
    buttonStyle: {
        backgroundColor: "#fff",
        color: "#4b70f5",
        padding: "1rem 2rem",
        border: "none",
        borderRadius: "30px",
        fontSize: "1rem",
        cursor: "pointer",
        fontWeight: "bold",
        transition: "background 0.3s, transform 0.3s",
    },
    graphicStyle: {
        flex: "1",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    circleContainerStyle: {
        position: "relative",
    },
    circleStyle: {
        position: "absolute",
        borderRadius: "50%",
        background: "rgba(255, 255, 255, 0.2)",
    },
};

export default AIIntegration;
